import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import {
	API_URL,
	docData,
	FIREBASE_AUTH,
	FIRESTORE,
	LOCAL_STORAGE_KEYS,
	LocalStorageService,
	ORGANIZATION_ID,
	USER,
} from '@context/frontend/common';
import { isExpired } from '@context/shared/types/common';
import {
	InviteSession,
	Organization,
} from '@context/shared/types/organization';
import {
	AppUser,
	AuthUser,
	getUserStatus,
	OrgUser,
	User,
	UserStatus,
} from '@context/shared/types/user';
import { getFirebaseErrorKey, sanitize } from '@context/shared/utils';
import {
	EmailAuthProvider,
	User as FirebaseUser,
	onAuthStateChanged,
	reauthenticateWithCredential,
	sendEmailVerification,
	signInWithCustomToken,
	signInWithEmailAndPassword,
	signOut,
	updatePassword,
	verifyBeforeUpdateEmail,
} from 'firebase/auth';
import {
	collection,
	CollectionReference,
	doc,
	DocumentReference,
	getDoc,
	Timestamp,
	updateDoc,
} from 'firebase/firestore';
import {
	BehaviorSubject,
	catchError,
	EMPTY,
	filter,
	firstValueFrom,
	map,
	merge,
	skip,
	Subject,
	Subscription,
	take,
	takeUntil,
	takeWhile,
} from 'rxjs';
import { SignInResult } from '../types';

@Injectable({ providedIn: 'root' })
export class AuthService<T extends AppUser = AppUser> {
	authUser: AuthUser | null = null;
	authUserRef: DocumentReference<AuthUser> | null = null;

	user: T | null = null;
	userRef: DocumentReference<T> | null = null;

	/**
	 * Live subscription to the signed-in user's document. Held so it can be torn
	 * down *before* the Firebase auth token is revoked on sign-out - otherwise the
	 * snapshot listener re-evaluates security rules with a null token and throws an
	 * uncaught "false for 'get'" permission error.
	 */
	private userChangesSub: Subscription | null = null;

	readonly firebaseUser = signal<FirebaseUser | null>(null);

	readonly organization = signal<Organization | null>(null);
	readonly organizationRef = signal<DocumentReference<Organization> | null>(
		null,
	);

	authenticated$ = new BehaviorSubject<boolean>(false);

	/**
	 * Emits when sign-in has settled to "needs organization selection" so login
	 * can complete without waiting for `authenticated$ === true` (which only
	 * happens after the user picks an org).
	 */
	private readonly needsOrganization$ = new Subject<void>();

	// Invite auth state
	readonly inviteId = signal<string | null>(null);

	/** External share link id when authenticated via share session */
	readonly shareExternalId = signal<string | null>(null);

	private readonly organizationId$ = inject(ORGANIZATION_ID);
	private readonly user$ = inject(USER);

	private readonly auth = inject(FIREBASE_AUTH);
	private readonly firestore = inject(FIRESTORE);
	private readonly router = inject(Router);
	private readonly http = inject(HttpClient);
	private readonly apiUrl = inject(API_URL);
	private readonly localStorage = inject(LocalStorageService);

	constructor() {
		onAuthStateChanged(this.auth, this.onAuthStateChanged.bind(this));
	}

	async onAuthStateChanged(firebaseUser: FirebaseUser | null) {
		if (!firebaseUser) return this.reset();
		this.firebaseUser.set(firebaseUser);

		// if auth is invite, then route to invite screen
		if (await this.determineInviteAuth(firebaseUser)) return;
		if (await this.determineShareAuth(firebaseUser)) return;

		const authSnapshot = await getDoc(
			doc(
				collection(
					this.firestore,
					'authUsers',
				) as CollectionReference<AuthUser>,
				firebaseUser.uid,
			),
		).catch((error) => {
			console.error('Auth user could not be found', error);
			throw error;
		});

		if (!authSnapshot.exists()) {
			throw new Error('Auth user could not be found');
		}

		this.authUser = authSnapshot.data() as AuthUser;
		this.authUserRef = authSnapshot.ref;

		let orgId: string | null = this.localStorage.getItem(
			LOCAL_STORAGE_KEYS.auth.organizationId,
		);

		const tasks: Promise<void>[] = [];
		if (this.authUser.role === 'super') {
			// use authUserRef to determine super user
			tasks.push(
				this.determineUser(this.authUserRef as DocumentReference<T>),
			);
		} else {
			// user authUser.users to determine non-super user
			const hasOne = this.authUser.users.length === 1;

			// if they only have one organization, use it.
			if (hasOne) {
				const { organization }: OrgUser = this.authUser.users[0];
				orgId = organization.id;
			}
		}

		// works for both super and non-super users
		if (!orgId) {
			this.authenticated$.next(false);
			this.needsOrganization$.next();
			return this.router.navigate(['/select-organization']);
		} else {
			return this.selectOrganization(orgId!)
				.catch((error) => {
					console.error(
						'Organization could not be determined',
						error,
					);
					this.signOut();
				})
				.then(() => this.authenticated$.next(true));
		}
	}

	async selectOrganization(orgId: string) {
		if (!this.authUser)
			throw new Error('Auth user required for organization selection');

		this.organizationId$.next(orgId);

		const storedOrgId = this.localStorage.getItem(
			LOCAL_STORAGE_KEYS.auth.organizationId,
		);

		if (storedOrgId !== orgId) {
			await this.updateCustomClaims(orgId);
			this.localStorage.setItem(
				LOCAL_STORAGE_KEYS.auth.organizationId,
				orgId,
			);
		}

		const orgRef = doc(
			this.firestore,
			'organizations',
			orgId,
		) as DocumentReference<Organization>;
		this.organizationRef.set(orgRef);

		const organizationSnapshot = await getDoc(
			this.organizationRef()!,
		).catch((error) => console.error('Organization not found', error));

		// @todo implement no organization state
		if (!organizationSnapshot || !organizationSnapshot.exists()) {
			return Promise.reject('No organization could be determined');
		}

		this.organization.set(organizationSnapshot.data() as Organization);

		let userRef: DocumentReference<T> | null = null;
		if (this.authUser?.role === 'super') {
			userRef = this.authUserRef as DocumentReference<T>;
		} else {
			userRef = this.authUser.users.find(
				(user) => user.organization.id === orgId,
			)?.user as DocumentReference<T>;
		}

		await this.determineUser(userRef as DocumentReference<T>);

		// reset the authenticated flag to true
		this.authenticated$.next(true);
	}

	async determineUser(userRef: DocumentReference<T>) {
		this.userRef = userRef;
		this.listenToUserChanges();
		this.user = (await getDoc(userRef as DocumentReference<T>)).data() as T;
		this.applyFirebaseUserData();
		this.user$.next(this.user);
	}

	listenToUserChanges() {
		if (!this.userRef) throw Error('UserRef is required for changes');

		// Avoid stacking listeners across re-auth/org switches.
		this.teardownUserChanges();

		this.userChangesSub = docData<T>(this.userRef)
			.pipe(
				skip(1),
				takeWhile(() => this.authenticated$.value),
				// A snapshot error during/after sign-out (token revoked) would
				// otherwise surface as an uncaught FirebaseError. Swallow it - the
				// teardown on sign-out is the intended path here.
				catchError(() => EMPTY),
			)
			.subscribe((value) => {
				const forceOut = (code?: string) =>
					this.signOut().then(() => {
						this.router.navigate(['/auth'], {
							queryParams: { alert: code ?? undefined },
						});
					});

				if (!value) return forceOut();

				this.user = value;
				this.applyFirebaseUserData();
				this.user$.next(this.user);

				// if an error returns from the check we need to sign out
				return this.checkUserStatus(getUserStatus(value)).catch(
					(error) => forceOut(getFirebaseErrorKey(error.code)),
				);
			});
	}

	/**
	 * Applies the needed information from the FirebaseUser data to the User and AuthUser
	 * objects. This ensures the email is correct regardless of how it was changed.
	 */
	applyFirebaseUserData() {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser || !this.user || !this.authUser) return;
		this.user.emailVerified = firebaseUser.emailVerified ?? false;
	}

	/**
	 * Signs the user into the app with the provided email and password. Waits until
	 * auth state has settled to either fully ready (org selected) or needs
	 * organization selection — so login does not stay pending through org pick
	 * and then race a second `/home` navigation.
	 *
	 * @param email the email to sign in with
	 * @param password the password to sign in with
	 */
	signInWithEmailAndPassword(
		email: string,
		password: string,
	): Promise<SignInResult> {
		return signInWithEmailAndPassword(this.auth, email, password).then(
			() =>
				new Promise<SignInResult>((resolve, reject) => {
					const settled$ = new Subject<void>();

					const ready$ = this.authenticated$.pipe(
						filter((value) => value === true),
						map((): SignInResult => ({ status: 'ready' })),
					);

					const needsOrg$ = this.needsOrganization$.pipe(
						map((): SignInResult => ({
							status: 'needsOrganization',
						})),
					);

					merge(ready$, needsOrg$)
						.pipe(take(1), takeUntil(settled$))
						.subscribe({
							next: (result) => {
								if (result.status === 'needsOrganization') {
									settled$.next();
									resolve(result);
									return;
								}

								const code = this.getAccountStatus();
								this.checkUserStatus(code)
									.then(() => {
										settled$.next();
										resolve(result);
									})
									.catch((error) => {
										settled$.next();
										this.signOut();
										reject(error);
									});
							},
						});
				}),
		);
	}

	checkUserStatus = (status: UserStatus | null) =>
		new Promise((resolve, reject) => {
			switch (status) {
				case 'disabled': {
					return reject({
						message: 'The account has been disabled',
						code: 'auth/account-disabled',
					});
				}
				case 'deleted': {
					return reject({
						message: 'The account has been deleted',
						code: 'auth/invalid-credential',
					});
				}
				default:
					return resolve(null);
			}
		});

	getAccountStatus(): UserStatus | null {
		if (!this.user) return null;
		return getUserStatus(this.user);
	}

	reauthenticateWithEmailAndPassword(email: string, password: string) {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) return Promise.reject();
		const credential = EmailAuthProvider.credential(email, password);
		return reauthenticateWithCredential(firebaseUser, credential);
	}

	updatePassword(newPassword: string) {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) return Promise.reject();
		return updatePassword(firebaseUser, newPassword);
	}

	signOut = () => {
		// Detach live listeners before the token is revoked so security rules
		// aren't re-evaluated against a null auth (which throws "false for 'get'").
		this.teardownUserChanges();
		return signOut(this.auth).then(() => this.reset());
	};

	private teardownUserChanges() {
		this.userChangesSub?.unsubscribe();
		this.userChangesSub = null;
	}

	reset() {
		this.teardownUserChanges();

		this.localStorage.removeItem(LOCAL_STORAGE_KEYS.auth.organizationId);
		this.organizationId$.next(null);
		this.organization.set(null);
		this.organizationRef.set(null);
		this.inviteId.set(null);
		this.shareExternalId.set(null);

		this.firebaseUser.set(null);

		this.authUser = null;
		this.authUserRef = null;

		this.user = null;
		this.userRef = null;
		this.user$.next(null);

		this.authenticated$.next(false);
	}

	/**
	 * Authenticates for viewing an invite by calling the backend and signing in with the custom token.
	 *
	 * @param inviteId Invite ID
	 * @param email Email from the invite
	 * @returns Promise that resolves when authentication is complete
	 */
	async authenticateForInvite(
		inviteId: string,
		email: string,
	): Promise<void> {
		try {
			const response = await firstValueFrom(
				this.http.post<{
					token: string;
					orgId: string;
					expiresAt: { _seconds: number; _nanoseconds: number };
				}>(`${this.apiUrl}/v1/users/invite/${inviteId}/authenticate`, {
					email,
				}),
			);
			// Sign in with the custom token
			await signInWithCustomToken(this.auth, response.token);

			// Setup will happen in onAuthStateChanged
		} catch (error) {
			console.error('Error authenticating for invite:', error);
			throw error;
		}
	}

	/**
	 * Checks whether the invite session is still valid.
	 * Used by guards to verify session hasn't expired.
	 *
	 * @param inviteId Invite ID
	 * @param orgId Organization ID
	 * @returns true if session exists and is not expired
	 */
	async isInviteSessionValid(
		inviteId: string,
		orgId: string,
	): Promise<boolean> {
		const sessionRef = doc(
			this.firestore,
			'organizations',
			orgId,
			'inviteSessions',
			inviteId,
		) as DocumentReference<InviteSession>;

		const snapshot = await getDoc(sessionRef);
		if (!snapshot.exists()) return false;

		const data = snapshot.data() as InviteSession;
		if (!data) return false;

		return !isExpired(data.expiresAt) && !data.deletedAt;
	}

	/**
	 * Updates the auth users organization specific profile with the changes. If email is updated, then
	 * it will require another sign in.
	 *
	 * @todo when implementing multiple tenants work through updating the email across multiple accounts
	 *
	 * @param data the data to update the auth user with
	 */
	async update(
		data: Partial<
			Pick<
				User,
				| 'email'
				| 'familyName'
				| 'givenName'
				| 'thumbnailUrl'
				| 'notifications'
				| 'updatedAt'
				| 'requestDeletedAt'
			>
		>,
	) {
		if (!this.userRef || !this.user || !this.firebaseUser)
			throw new Error('User ref could not be found');

		let emailUpdated = false;
		if (data.email && data.email !== this.user.email) {
			// we need to change the auth email first before attempting to update the org user object
			await this.updateEmail(data.email)?.catch((error) => {
				console.error(error);
				throw new Error(
					'AuthService.update: Email could not be updated',
				);
			});
			emailUpdated = true;
		}

		data.updatedAt = Timestamp.now();
		await updateDoc(this.userRef, sanitize(data));

		// the user needs to be signed out to apply updates
		if (emailUpdated)
			return Promise.resolve({ user: this.user, signOut: true });

		return { user: this.user, signOut: false };
	}

	sendVerificationEmail() {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) return Promise.reject();
		return sendEmailVerification(firebaseUser);
	}

	/**
	 * Updates the auth user data to correlate to the new email being set by the
	 * user. Currently we update ALL user accounts tied to this auth user.
	 *
	 * @todo implement account email linking/unlinking for when multi-org is available
	 *
	 * @param email the email to update the auth user with
	 */
	async updateEmail(email: string) {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser || !this.authUserRef) return;
		return verifyBeforeUpdateEmail(firebaseUser, email);
	}

	/**
	 * Fetches the list of organizations the current user can select.
	 * Does not require an organization in context (for select-organization page).
	 */
	getMyOrganizations() {
		return this.http.get<Organization[]>(
			`${this.apiUrl}/v1/users/me/organizations`,
		);
	}

	/**
	 * Updates custom claims on the Firebase Auth token with orgId and role.
	 * After updating claims, the ID token is refreshed to include the new claims.
	 *
	 * @param orgId Organization ID to set in claims
	 * @param role User role (user, admin, owner, super)
	 */
	async updateCustomClaims(orgId: string): Promise<void> {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) {
			console.warn('AuthService.updateCustomClaims: Missing user');
			return;
		}

		try {
			// Call backend to set custom claims
			await firstValueFrom(
				this.http.get(`${this.apiUrl}/v1/users/claims/update`, {
					headers: { Organization: orgId },
				}),
			);

			// Refresh the ID token to get the new claims
			await this.refreshIdToken();
		} catch (error) {
			console.error('AuthService.updateCustomClaims', error);
			// Don't throw - allow app to continue even if claims update fails
			// This is a non-critical operation for initial auth flow
		}
	}

	/**
	 * Refreshes the Firebase Auth ID token to get updated custom claims.
	 * This should be called after updating custom claims on the backend.
	 */
	async refreshIdToken(): Promise<void> {
		const firebaseUser = this.firebaseUser();
		if (!firebaseUser) {
			console.warn('AuthService.refreshIdToken: No Firebase user');
			return;
		}

		try {
			// Force refresh the token to get updated claims
			await firebaseUser.getIdToken(true);
		} catch (error) {
			console.error('AuthService.refreshIdToken', error);
			throw error;
		}
	}

	async determineInviteAuth(firebaseUser: FirebaseUser): Promise<boolean> {
		const idTokenResult = await firebaseUser.getIdTokenResult();
		const isInviteAuth =
			idTokenResult.claims['role'] === 'invite' &&
			idTokenResult.claims['inviteId'] != null;

		if (!isInviteAuth) return false;

		this.firebaseUser.set(firebaseUser);
		const inviteId = idTokenResult.claims['inviteId'] as string;
		const orgId = idTokenResult.claims['orgId'] as string;
		this.inviteId.set(inviteId);
		this.shareExternalId.set(null);
		this.organizationId$.next(orgId);

		this.authenticated$.next(true);

		return true;
	}

	async determineShareAuth(firebaseUser: FirebaseUser): Promise<boolean> {
		const idTokenResult = await firebaseUser.getIdTokenResult();
		const isShareAuth =
			idTokenResult.claims['role'] === 'share' &&
			idTokenResult.claims['shareExternalId'] != null;

		if (!isShareAuth) return false;

		this.firebaseUser.set(firebaseUser);
		const orgId = idTokenResult.claims['orgId'] as string;
		const shareId = idTokenResult.claims['shareExternalId'] as string;
		this.shareExternalId.set(shareId);
		this.inviteId.set(null);
		this.organizationId$.next(orgId);

		this.authenticated$.next(true);

		return true;
	}
}
